import { Component, forwardRef, Output, EventEmitter, NgZone, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Component({
  selector: 'app-location-search',
  standalone: true,
  templateUrl: './location-search.component.html',
  styleUrls: ['./location-search.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationSearchComponent),
      multi: true
    }
  ],
  imports: [
    CommonModule,
    FormsModule,
    AutoCompleteModule
  ]
})
export class LocationSearchComponent implements ControlValueAccessor {

  @ViewChild('placeAutocomplete') private placeAutocomplete?: AutoComplete;

  searchPlaceResults: any[] = [];
  selectedValue: any;
  isResolvingGoogleMapsUrl = false;
  private readonly placeDetailFields = [
    'address_components',
    'formatted_address',
    'formatted_phone_number',
    'geometry',
    'international_phone_number',
    'name',
    'photos',
    'place_id',
    'website'
  ];

  @Output() placeSelected = new EventEmitter<google.maps.places.PlaceResult>();
  @Output() googleMapsUrlDetected = new EventEmitter<string>();

  private onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private http: HttpClient, private ngZone: NgZone) {}

  writeValue(value: any): void {
    this.selectedValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  searchPlaces(event: { query: string; }) {
    const query = event.query.trim();
    const coordRegex = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;
    const isCoordinates = coordRegex.exec(query);

    if (this.isGoogleMapsUrl(query)) {
      this.googleMapsUrlDetected.emit(query);
      this.resolveGoogleMapsUrl(query);
    } else if (isCoordinates) {
      const lat = parseFloat(isCoordinates[1]);
      const lng = parseFloat(isCoordinates[2]);
      this.reverseGeocodeCoordinates(lat, lng, true);
    } else if (query.includes('+')) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: query }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results) {
          this.searchPlaceResults = results.map(result => ({
            name: result.formatted_address,
            place: result
          }));
        } else {
          this.performAutocompleteSearch(query);
        }
      });
    } else {
      this.performAutocompleteSearch(query);
    }
  }

  private isGoogleMapsUrl(query: string): boolean {
    if (!query.startsWith('http://') && !query.startsWith('https://')) {
      return false;
    }
    try {
      const parsed = new URL(query);
      return [
        'maps.app.goo.gl',
        'www.google.com',
        'google.com',
        'maps.google.com'
      ].includes(parsed.hostname.toLowerCase());
    } catch {
      return false;
    }
  }

  private resolveGoogleMapsUrl(query: string): void {
    const extractorApiUrl = `https://google-maps-url-extractor-113166165184.europe-west1.run.app?shortUrl=${encodeURIComponent(query)}`;
    this.isResolvingGoogleMapsUrl = true;
    this.searchPlaceResults = [];
    this.http.get<{ lat?: string; lng?: string; fullUrl?: string }>(extractorApiUrl).subscribe({
      next: (response) => {
        const coordinates = this.extractCoordinatesFromGoogleMapsResponse(response);
        if (coordinates) {
          this.reverseGeocodeCoordinates(coordinates.lat, coordinates.lng, true);
          return;
        }
        this.isResolvingGoogleMapsUrl = false;
        this.performAutocompleteSearch(this.googleMapsUrlFallbackQuery(response.fullUrl || query));
      },
      error: () => {
        this.isResolvingGoogleMapsUrl = false;
        this.performAutocompleteSearch(this.googleMapsUrlFallbackQuery(query));
      }
    });
  }

  private extractCoordinatesFromGoogleMapsResponse(response: { lat?: string; lng?: string; fullUrl?: string }): { lat: number; lng: number } | null {
    const lat = this.validGoogleMapsCoordinate(response.lat);
    const lng = this.validGoogleMapsCoordinate(response.lng);
    if (lat != null && lng != null) {
      return { lat, lng };
    }

    const fullUrl = response.fullUrl;
    if (!fullUrl) {
      return null;
    }

    let decodedUrl = fullUrl;
    try {
      decodedUrl = decodeURIComponent(fullUrl);
    } catch {
      // The expanded Maps URL is still usable when it contains an invalid escape sequence.
    }
    const placeMatch = decodedUrl.match(/\/place\/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/i);
    if (placeMatch) {
      return {
        lat: Number(placeMatch[1]),
        lng: Number(placeMatch[2])
      };
    }

    const dataMatch = decodedUrl.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/i);
    if (dataMatch) {
      return {
        lat: Number(dataMatch[1]),
        lng: Number(dataMatch[2])
      };
    }

    return null;
  }

  private validGoogleMapsCoordinate(value: unknown): number | null {
    if (value == null || (typeof value === 'string' && !value.trim())) {
      return null;
    }
    const coordinate = Number(value);
    return Number.isFinite(coordinate) ? coordinate : null;
  }

  private googleMapsUrlFallbackQuery(query: string): string {
    try {
      const parsed = new URL(query);
      const searchQuery = parsed.searchParams.get('q')
        || parsed.searchParams.get('query')
        || parsed.searchParams.get('destination')
        || parsed.searchParams.get('origin');
      if (searchQuery?.trim()) {
        return decodeURIComponent(searchQuery).trim();
      }

      const placeMatch = decodeURIComponent(parsed.pathname).match(/\/place\/([^/]+)/i);
      if (placeMatch?.[1]) {
        return placeMatch[1].replace(/\+/g, ' ').trim();
      }
    } catch {
      return query;
    }
    return query;
  }

  private reverseGeocodeCoordinates(lat: number, lng: number, autoSelectFirstResult = false): void {
    const latLng = new google.maps.LatLng(lat, lng);
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      this.ngZone.run(() => {
        if (status === google.maps.GeocoderStatus.OK && results?.length) {
          this.searchPlaceResults = results.map(result => ({
            name: result.formatted_address,
            place: result
          }));
          if (autoSelectFirstResult) {
            // A URL or coordinate lookup is an explicit selection. Apply it directly
            // instead of waiting for the autocomplete overlay to select a result.
            this.selectGeocodedCoordinates(results[0], latLng);
            this.isResolvingGoogleMapsUrl = false;
            this.hideAutocompleteOverlay();
          }
        } else {
          this.searchPlaceResults = [];
          if (autoSelectFirstResult) {
            this.isResolvingGoogleMapsUrl = false;
            this.hideAutocompleteOverlay();
          }
        }
      });
    });
  }

  private selectGeocodedCoordinates(result: google.maps.GeocoderResult, coordinates: google.maps.LatLng): void {
    const place = {
      ...result,
      name: result.formatted_address,
      geometry: {
        ...result.geometry,
        // Keep the exact point supplied by Google Maps, rather than the broader
        // reverse-geocoded address geometry.
        location: coordinates
      }
    } as unknown as google.maps.places.PlaceResult;

    this.handlePlaceSelection(place);
  }

  private hideAutocompleteOverlay(): void {
    // PrimeNG opens the overlay before the asynchronous URL expansion finishes.
    // Deferring hide lets its own paste handler complete first.
    setTimeout(() => this.placeAutocomplete?.hide());
  }

  performAutocompleteSearch(query: string) {
    if (!query) {
      this.searchPlaceResults = [];
      return;
    }
    const autocompleteService = new google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions({ input: query }, (predictions, status) => {
      this.ngZone.run(() => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
          this.searchPlaceResults = [];
          return;
        }
        this.searchPlaceResults = predictions.map(p => ({ name: p.description, prediction: p }));
      });
    });
  }

  onSelect(event: { originalEvent: Event, value: any }) {
    const selectedValue = event.value;

    if (selectedValue && selectedValue.place) { // From Geocoder result
      this.loadPlaceDetails(selectedValue.place.place_id, selectedValue.place);
    } else if (selectedValue && selectedValue.prediction) { // From Autocomplete prediction
      this.loadPlaceDetails(selectedValue.prediction.place_id);
    }
  }

  private loadPlaceDetails(placeId?: string, fallbackPlace?: google.maps.places.PlaceResult) {
    if (!placeId) {
      if (fallbackPlace) {
        this.handlePlaceSelection(fallbackPlace);
      }
      return;
    }

    const placesService = new google.maps.places.PlacesService(document.createElement('div'));
    placesService.getDetails({ placeId, fields: this.placeDetailFields }, (place, status) => {
      this.ngZone.run(() => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          this.handlePlaceSelection({
            ...fallbackPlace,
            ...place
          });
          return;
        }

        if (fallbackPlace) {
          this.handlePlaceSelection(fallbackPlace);
        }
      });
    });
  }

  private handlePlaceSelection(place: google.maps.places.PlaceResult) {
    this.selectedValue = place.formatted_address;
    this.searchPlaceResults = [];
    this.onChange(this.selectedValue);
    this.onTouched();
    this.placeSelected.emit(place);
  }
}
